import { NavLink, useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Users, Package, ReceiptText, FileText, CreditCard,
  Wallet, BarChart3, Calculator, Warehouse, HardHat, Briefcase, Settings,
  Search, Bell, Moon, Sun, LogOut, Sparkles, Menu, X, ChevronRight,
  UserCog, KanbanSquare, Wrench, Truck, ShieldCheck, Store, Building
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import AIChat from "@/components/AIChat";
import GlobalSearch from "@/components/GlobalSearch";

const SIDEBAR = [
  {
    section: "General",
    items: [
      { to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true },
      { to: "/app/parties", icon: Users, label: "Parties" },
      { to: "/app/items", icon: Package, label: "Items" },
      { to: "/app/sales", icon: ReceiptText, label: "Sales" },
      { to: "/app/purchases", icon: Truck, label: "Purchases" },
      { to: "/app/reports", icon: BarChart3, label: "Reports" },
    ],
  },
  {
    section: "Accounting",
    items: [
      { to: "/app/cash-bank", icon: Wallet, label: "Cash & Bank" },
      { to: "/app/gst", icon: Calculator, label: "GST" },
      { to: "/app/expenses", icon: Wallet, label: "Expenses" },
      { to: "/app/inventory", icon: Warehouse, label: "Inventory" },
    ],
  },
  {
    section: "Business Tools",
    items: [
      { to: "/app/projects", icon: HardHat, label: "Projects" },
      { to: "/app/employees", icon: Briefcase, label: "Employees" },
      { to: "/app/crm", icon: KanbanSquare, label: "CRM" },
      { to: "/app/services", icon: Wrench, label: "Services" },
    ],
  },
  {
    section: "Account",
    items: [
      { to: "/app/subscription", icon: ShieldCheck, label: "Subscription" },
      { to: "/app/settings", icon: Settings, label: "Settings" },
    ],
  },
];

export default function Layout({ children }) {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogout = async () => { await logout(); nav("/login"); };
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";

  return (
    <div className="min-h-screen bg-muted/40">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed top-0 left-0 bottom-0 w-60 sidebar-glass border-r border-border z-40 flex flex-col transform transition-transform duration-300 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="h-16 flex items-center gap-2 px-5 border-b border-border/60">
          <div className="w-9 h-9 rounded-lg bg-primary text-primary-foreground grid place-items-center font-heading font-bold text-sm">BP</div>
          <div>
            <div className="font-heading font-bold text-base leading-tight">TheBillPro</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Business OS</div>
          </div>
          <button className="ml-auto lg:hidden" onClick={() => setOpen(false)}>
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-4">
          {SIDEBAR.map((sec) => (
            <div key={sec.section}>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-3 mb-1.5">{sec.section}</div>
              <div className="space-y-0.5">
                {sec.items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    data-testid={`sidebar-${n.label.toLowerCase().replace(/\s+/g, "-").replace("&","and")}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                        isActive ? "bg-primary text-primary-foreground font-semibold shadow-sm" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`
                    }
                  >
                    <n.icon size={16} strokeWidth={1.75} />
                    <span>{n.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
          {isAdmin && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-3 mb-1.5">Super Admin</div>
              <NavLink to="/admin" data-testid="sidebar-admin"
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted hover:text-foreground">
                <ShieldCheck size={16} /> Admin Panel <ChevronRight size={12} className="ml-auto" />
              </NavLink>
            </div>
          )}
        </nav>
        <div className="p-3 border-t border-border/60 text-xs text-muted-foreground">v1.0 · © TheBillPro</div>
      </aside>

      {/* Header */}
      <header className="h-16 fixed top-0 right-0 left-0 lg:left-60 glass border-b border-border/60 z-30 flex items-center px-4 sm:px-6 gap-3">
        <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="header-menu-btn">
          <Menu size={20} />
        </button>
        <button
          onClick={() => setSearchOpen(true)}
          data-testid="header-search-btn"
          className="flex-1 max-w-md flex items-center gap-2 h-9 px-3 rounded-lg bg-muted/60 hover:bg-muted border border-border/40 text-sm text-muted-foreground transition-colors"
        >
          <Search size={16} />
          <span>Search customers, invoices, products…</span>
          <kbd className="ml-auto text-[10px] hidden sm:inline bg-background border px-1.5 py-0.5 rounded">Ctrl+K</kbd>
        </button>
        <div className="ml-auto flex items-center gap-1.5">
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="header-theme-toggle" aria-label="Toggle theme">
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </Button>
          <Button variant="ghost" size="icon" data-testid="header-notifications" aria-label="Notifications">
            <Bell size={18} />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="header-user-profile" className="flex items-center gap-2 pl-1.5 pr-2.5 py-1 rounded-full hover:bg-muted transition-colors">
                <Avatar className="w-8 h-8"><AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">{user?.name?.[0]?.toUpperCase() || "U"}</AvatarFallback></Avatar>
                <span className="text-sm font-medium hidden sm:inline">{user?.name || "User"}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-sm font-semibold">{user?.name}</div>
                <div className="text-xs text-muted-foreground">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => nav("/app/subscription")}><ShieldCheck size={14} className="mr-2" /> Subscription</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/app/settings")}><UserCog size={14} className="mr-2" /> Business Settings</DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => nav("/admin")}><ShieldCheck size={14} className="mr-2" /> Super Admin</DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout} className="text-destructive"><LogOut size={14} className="mr-2" /> Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Trial banner */}
      {sub?.status === "trial" && (
        <div className="fixed top-16 right-0 left-0 lg:left-60 z-20 bg-primary text-primary-foreground px-4 sm:px-6 py-2 text-sm flex items-center gap-3" data-testid="trial-banner">
          <Sparkles size={14} className="flex-shrink-0" />
          <span className="flex-1 truncate">Your free trial ends in <b>{sub.days_remaining ?? "…"} days</b>. Upgrade to keep uninterrupted access.</span>
          <Link to="/app/subscription" className="underline font-semibold flex-shrink-0" data-testid="trial-banner-cta">Upgrade →</Link>
        </div>
      )}
      {sub?.status === "expired" || sub?.status === "past_due" ? (
        <div className="fixed top-16 right-0 left-0 lg:left-60 z-20 bg-destructive text-destructive-foreground px-4 sm:px-6 py-2 text-sm flex items-center gap-3">
          Your subscription has expired. Please renew to continue creating invoices.
          <Link to="/app/subscription" className="underline font-semibold ml-auto">Renew →</Link>
        </div>
      ) : null}

      {/* Main */}
      <main className={`lg:pl-60 pt-16 min-h-screen ${sub?.status === "trial" || sub?.status === "expired" ? "pt-[104px]" : ""}`}>
        <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8 fade-up">{children}</div>
      </main>

      <AIChat />
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      {open && (
        <div className="fixed inset-0 bg-foreground/30 backdrop-blur-sm z-30 lg:hidden" onClick={() => setOpen(false)} />
      )}
    </div>
  );
}
